<template>
  <div class="admin-view">
    <div class="view-header">
      <h2>Clients</h2>
      <button @click="openCreate" class="btn btn-primary">+ New Client</button>
    </div>

    <p v-if="loadError" class="load-error">{{ loadError }}</p>
    <div v-if="loading" class="loading-state">Loading clients…</div>
    <div v-else-if="clients.length === 0 && !loadError" class="empty">No clients yet.</div>

    <!-- ── Inline client rows (CL1): thumbnail · info in-line · click to view ── -->
    <div class="cl-list">
      <div
        v-for="client in clients"
        :key="client.id"
        class="cl-row"
        role="button"
        tabindex="0"
        @click="openView(client)"
        @keydown.enter="openView(client)"
      >
        <div class="cl-thumb" :class="{ 'has-photo': !!client.photo_s3_key }">
          <img v-if="client.photo_s3_key" :src="photoUrl(client.photo_s3_key)" :alt="client.name" />
          <span v-else class="cl-thumb-initials">{{ initials(client) }}</span>
        </div>

        <div class="cl-main">
          <div class="cl-name-line">
            <strong>{{ client.name }}</strong>
            <span v-if="client.nickname" class="cl-nick">“{{ client.nickname }}”</span>
            <span v-if="!client.email" class="cl-chip cl-chip-record" title="No login — record only">record</span>
          </div>
          <div class="cl-facts">
            <span v-if="client.email" class="cl-fact">{{ client.email }}</span>
            <span v-if="client.phone" class="cl-fact">{{ client.phone }}</span>
            <span v-if="cityLine(client)" class="cl-fact">{{ cityLine(client) }}</span>
            <span v-if="birthdateLine(client)" class="cl-fact">🎂 {{ birthdateLine(client) }}</span>
          </div>
        </div>

        <SocialLinks v-if="asLinks(client.socials).length" :socials="asLinks(client.socials)" class="cl-row-socials" @click.stop />

        <div class="cl-row-actions" @click.stop>
          <button @click="openEdit(client)" class="btn btn-outline btn-sm">Edit</button>
        </div>
      </div>
    </div>

    <!-- ── Client View (read-only, CL1) ── -->
    <ModalShell v-model:open="viewOpen" :title="viewClient?.name || 'Client'" max-width="520px">
      <div v-if="viewClient" class="cl-view">
        <div class="cl-view-head">
          <div class="cl-thumb lg" :class="{ 'has-photo': !!viewClient.photo_s3_key }">
            <img v-if="viewClient.photo_s3_key" :src="photoUrl(viewClient.photo_s3_key)" :alt="viewClient.name" />
            <span v-else class="cl-thumb-initials">{{ initials(viewClient) }}</span>
          </div>
          <div>
            <h4 class="cl-view-name">
              {{ viewClient.name }}
              <span v-if="viewClient.nickname" class="cl-nick">“{{ viewClient.nickname }}”</span>
            </h4>
            <p v-if="viewClient.gender" class="cl-view-sub">{{ typeLabel(viewClient.gender) }}</p>
            <SocialLinks v-if="asLinks(viewClient.socials).length" :socials="asLinks(viewClient.socials)" />
          </div>
        </div>

        <dl class="cl-view-grid">
          <template v-if="viewClient.email"><dt>Email</dt><dd>{{ viewClient.email }}</dd></template>
          <template v-else><dt>Login</dt><dd class="cl-muted">No login account yet</dd></template>
          <template v-if="viewClient.phone"><dt>Phone</dt><dd>{{ viewClient.phone }}</dd></template>
          <template v-if="addressBlock(viewClient)"><dt>Address</dt><dd>{{ addressBlock(viewClient) }}</dd></template>
          <template v-if="birthdateLine(viewClient)"><dt>Birthday</dt><dd>🎂 {{ birthdateLine(viewClient) }}</dd></template>
          <template v-for="sd in (viewClient.special_dates || [])" :key="sd.label + sd.date">
            <dt>{{ sd.label }}</dt><dd>{{ formatSpecialDate(sd.date) }}</dd>
          </template>
        </dl>

        <p class="cl-view-added">Added {{ formatDate(viewClient.created_at) }}</p>

        <div class="modal-actions">
          <button type="button" @click="viewOpen = false" class="btn btn-outline">Close</button>
          <button type="button" @click="editFromView" class="btn btn-primary">Edit</button>
        </div>
      </div>
    </ModalShell>

    <!-- ── Client Modal (shared Create / Edit) ── -->
    <div v-if="modalOpen" class="modal" @click.self="closeModal">
      <div class="modal-content cl-modal">
        <h3>{{ isEdit ? 'Edit Client' : 'New Client' }}</h3>
        <form @submit.prevent="save" novalidate autocomplete="off">
          <!-- autocomplete="off" on the form + a token per address field below
               stops the browser's "save this address?" prompt on these admin
               records (which describe the CLIENT, not the operator). -->
          <!-- Photo (CL4) — centered at the top -->
          <FormSection photo>
            <PhotoUploadField
              v-model="form.photo_s3_key"
              kind="client"
              :ref-id="editId"
              :paste-active="modalOpen"
              round
            />
          </FormSection>

          <!-- Name -->
          <FormSection>
            <div class="cl-grid-2">
              <div class="form-group">
                <label>First Name <span class="optional">(optional)</span></label>
                <input v-model.trim="form.first_name" maxlength="120" @input="syncDisplayName" />
              </div>
              <div class="form-group">
                <label>Last Name <span class="optional">(optional)</span></label>
                <input v-model.trim="form.last_name" maxlength="120" @input="syncDisplayName" />
              </div>
            </div>
            <div class="cl-grid-2">
              <div class="form-group">
                <label>Display Name</label>
                <input v-model.trim="form.name" maxlength="255" @input="displayNameTouched = true" placeholder="Auto from first + last" />
              </div>
              <div class="form-group">
                <label>Nickname <span class="optional">(optional)</span></label>
                <input v-model.trim="form.nickname" maxlength="120" placeholder="e.g. The Smiths" />
              </div>
            </div>
            <div class="form-group">
              <label>Type <span class="optional">(optional)</span></label>
              <select v-model="form.gender">
                <option value="">—</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="group">Group</option>
              </select>
            </div>
            <div class="form-group">
              <label>Birthdate <span class="optional">(optional — leave year blank if unknown)</span></label>
              <BirthdatePicker
                v-model:month="form.birth_month"
                v-model:day="form.birth_day"
                v-model:year="form.birth_year"
              />
            </div>
          </FormSection>

          <!-- Contact (CL2: both optional) -->
          <FormSection title="Contact">
            <EmailInput v-model="form.email" label="Email" :optional="true" @update:valid="emailValid = $event" />
            <PhoneInput v-model="form.phone" label="Phone" :optional="true" />
          </FormSection>

          <!-- Address -->
          <FormSection title="Address" optional>
            <div class="form-group">
              <label>Street</label>
              <input v-model.trim="form.address_line1" maxlength="255" placeholder="Address line 1" autocomplete="off" name="cl-addr1" />
            </div>
            <div class="form-group">
              <input v-model.trim="form.address_line2" maxlength="255" placeholder="Address line 2 (apt, suite…)" autocomplete="off" name="cl-addr2" />
            </div>
            <div class="cl-grid-2">
              <div class="form-group">
                <label>City</label>
                <input v-model.trim="form.city" maxlength="120" autocomplete="off" name="cl-city" />
              </div>
              <div class="form-group">
                <label>State / Region</label>
                <input v-model.trim="form.state" maxlength="120" autocomplete="off" name="cl-state" />
              </div>
            </div>
            <div class="cl-grid-2">
              <div class="form-group">
                <label>Postal Code</label>
                <input v-model.trim="form.postal_code" maxlength="20" autocomplete="off" name="cl-postal" />
              </div>
              <div class="form-group">
                <label>Country</label>
                <input v-model.trim="form.country" maxlength="120" autocomplete="off" name="cl-country" />
              </div>
            </div>
          </FormSection>

          <!-- Socials (CL3: brand icons via shared editor) -->
          <FormSection>
            <SocialLinksEditor v-model="socialLinks" label="Social Media" hint="(optional)" />
            <small v-if="errors.socials" class="cl-err">{{ errors.socials }}</small>
          </FormSection>

          <!-- Special dates (CL5) -->
          <FormSection>
            <div class="cl-sd-head">
              <h4 class="cl-section-title">Special Dates <span class="optional">(optional)</span></h4>
              <button type="button" class="btn btn-sm btn-outline" @click="addSpecialDate">+ Add date</button>
            </div>
            <div v-for="(sd, idx) in form.special_dates" :key="idx" class="cl-sd-row">
              <input
                v-model.trim="sd.label"
                class="cl-sd-label"
                list="cl-sd-suggestions"
                placeholder="Anniversary, Graduation…"
                maxlength="120"
                autocomplete="off"
              />
              <input v-model="sd.date" type="date" class="cl-sd-date" />
              <button type="button" class="cl-social-remove" @click="removeSpecialDate(idx)" title="Remove">✕</button>
            </div>
            <datalist id="cl-sd-suggestions">
              <option value="Anniversary" />
              <option value="Graduation" />
              <option value="Wedding" />
              <option value="Engagement" />
            </datalist>
            <small v-if="errors.special_dates" class="cl-err">{{ errors.special_dates }}</small>
          </FormSection>

          <!-- Password (CL4: optional even on create) -->
          <FormSection title="Login">
            <div class="form-group">
              <label>
                Password
                <span class="optional">{{ isEdit ? '(leave blank to keep existing)' : '(optional — assign later)' }}</span>
              </label>
              <input
                v-model="form.password"
                type="password"
                minlength="8"
                autocomplete="new-password"
                :placeholder="isEdit ? '••••••••' : 'Set a password to enable login'"
                @blur="touch('password')"
              />
              <small v-if="errors.password" class="cl-err">{{ errors.password }}</small>
              <small v-else-if="passwordNeedsEmail" class="cl-err">A password needs an email to log in with.</small>
            </div>
          </FormSection>

          <p v-if="modalError" class="error">{{ modalError }}</p>
          <div class="modal-actions">
            <button type="button" @click="closeModal" class="btn btn-outline">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving || !isValid">
              {{ saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save Changes' : 'Create') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed } from 'vue'
import { useAdminApi, photoUrl } from '@/composables/useAdminApi'
import SocialLinks from '@/components/SocialLinks.vue'
import SocialLinksEditor from '@/components/SocialLinksEditor.vue'
import PhotoUploadField from '@/components/PhotoUploadField.vue'
import ModalShell from '@/components/ModalShell.vue'
import PhoneInput from '@/components/PhoneInput.vue'
import EmailInput from '@/components/EmailInput.vue'
import FormSection from '@/components/FormSection.vue'
import BirthdatePicker from '@/components/BirthdatePicker.vue'
import { validatePhone } from '@/lib/validatePhone'
import { validateUrl } from '@/lib/validateUrl'
import { validateDate } from '@/lib/validateDate'

interface Social { platform: string; handle: string }
interface SpecialDate { label: string; date: string }
interface Client {
  id: number
  name: string
  email?: string | null
  first_name?: string | null
  last_name?: string | null
  nickname?: string | null
  gender?: string | null
  phone?: string | null
  address_line1?: string | null
  address_line2?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  country?: string | null
  birth_month?: number | null
  birth_day?: number | null
  birth_year?: number | null
  socials?: Social[]
  special_dates?: SpecialDate[]
  photo_s3_key?: string | null
  created_at: string
}

const { apiFetch, formatDate } = useAdminApi()

const clients   = ref<Client[]>([])
const loadError = ref('')
const loading   = ref(true)

const loadClients = async () => {
  const res  = await apiFetch('/admin/clients')
  const data = await res.json()
  if (res.ok) clients.value = (data.clients as Client[]).map(c => ({
    ...c, socials: c.socials ?? [], special_dates: c.special_dates ?? [],
  }))
}

onMounted(async () => {
  loading.value = true
  try { await loadClients() }
  catch (e: any) { if (e.message !== 'Unauthorized') loadError.value = 'Failed to load clients' }
  finally { loading.value = false }
})

// ── List card helpers ─────────────────────────────────────────────────────────
const cityLine = (c: Client) => [c.city, c.state, c.country].filter(Boolean).join(', ')
const addressBlock = (c: Client) =>
  [c.address_line1, c.address_line2, [c.city, c.state, c.postal_code].filter(Boolean).join(' '), c.country]
    .filter(Boolean).join(', ')
const MONTH_LABELS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const birthdateLine = (c: Client) => {
  if (!c.birth_month && !c.birth_day) return ''
  const mon = c.birth_month ? MONTH_LABELS[c.birth_month] : ''
  const parts = [mon, c.birth_day ? String(c.birth_day) : ''].filter(Boolean).join(' ')
  return c.birth_year ? `${parts}, ${c.birth_year}` : parts
}
const typeLabel = (g: string) => ({ male: 'Male', female: 'Female', group: 'Group' }[g] ?? g)
const initials = (c: Client) => {
  const base = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.name || c.email || '?'
  return base.trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('') || '?'
}
const formatSpecialDate = (d: string) => {
  const [y, m, day] = d.split('T')[0].split('-').map(Number)
  return new Date(y, m - 1, day).toLocaleDateString()
}

// SocialLinks/SocialLinksEditor speak { platform, url }; the client model + API
// speak { platform, handle }. Bridge at the boundary so the API stays unchanged.
const asLinks = (socials?: Social[]) => (socials ?? []).map(s => ({ platform: s.platform, url: s.handle }))

// ── Modal state ────────────────────────────────────────────────────────────────
const modalOpen = ref(false)
const isEdit    = ref(false)
const editId    = ref(0)
const saving    = ref(false)
const modalError = ref('')
const displayNameTouched = ref(false)

const blankForm = () => ({
  first_name: '', last_name: '', name: '', nickname: '', gender: '',
  email: '', phone: '',
  address_line1: '', address_line2: '', city: '', state: '', postal_code: '', country: '',
  birth_month: null as number | null, birth_day: null as number | null, birth_year: null as number | null,
  socials: [] as Social[],
  special_dates: [] as SpecialDate[],
  photo_s3_key: null as string | null,
  password: '',
})
const form = reactive(blankForm())

// Editor <-> form bridge for socials ({platform,url} <-> {platform,handle}).
const socialLinks = computed<{ platform: string; url: string }[]>({
  get: () => form.socials.map(s => ({ platform: s.platform, url: s.handle })),
  set: (v) => { form.socials = v.map(l => ({ platform: l.platform, handle: l.url })) },
})

// ── Validation (reactive) ──────────────────────────────────────────────────────
// Email + URL + date rules now come from the shared validators. Email validity
// is reported by the EmailInput component (which renders its own inline error);
// we track it here only to gate the submit button, exactly like phone.
const emailValid = ref(true)

const touched = reactive<Record<string, boolean>>({})
const touch = (field: string) => { touched[field] = true }

const hasPassword = computed(() => !!form.password && form.password.length > 0)
const passwordNeedsEmail = computed(() => hasPassword.value && !form.email && touched.password)

const rules = computed(() => {
  const r: Record<string, string> = {}
  // Phone optional; when present it must pass the shared rule. PhoneInput renders
  // the inline error itself (touched-on-blur); keeping it in rules also gates isValid.
  if (form.phone) { const pr = validatePhone(form.phone); if (!pr.ok) r.phone = pr.error || 'Phone number format is invalid' }
  // Password optional; if set, must be ≥8 and needs an email to be a login.
  if (hasPassword.value && form.password.length < 8) r.password = 'At least 8 characters'
  if (hasPassword.value && !form.email) r.password = 'A password needs an email to log in with'
  if (form.socials.some(s => s.platform === 'other' && s.handle && !validateUrl(s.handle).ok)) {
    r.socials = 'A “Website / Other” link must be a full URL (https://…)'
  }
  for (const sd of form.special_dates) {
    if ((sd.label && !sd.date) || (!sd.label && sd.date)) { r.special_dates = 'Each special date needs both a label and a date'; break }
    if (sd.date && !validateDate(sd.date).ok) { r.special_dates = 'Special date is invalid'; break }
  }
  return r
})

// Email is gated by the EmailInput's reported validity (blank optional = valid).
const isValid = computed(() => emailValid.value && Object.keys(rules.value).length === 0)

const errors = computed<Record<string, string>>(() => {
  const shown: Record<string, string> = {}
  for (const [field, msg] of Object.entries(rules.value)) {
    if (touched[field]) shown[field] = msg
  }
  return shown
})

const resetForm = () => {
  Object.assign(form, blankForm())
  Object.keys(touched).forEach(k => delete touched[k])
  displayNameTouched.value = false
  modalError.value = ''
}

const openCreate = () => {
  resetForm()
  isEdit.value = false
  editId.value = 0
  modalOpen.value = true
}

const openEdit = (c: Client) => {
  resetForm()
  isEdit.value = true
  editId.value = c.id
  Object.assign(form, {
    first_name: c.first_name ?? '', last_name: c.last_name ?? '', name: c.name ?? '',
    nickname: c.nickname ?? '', gender: c.gender ?? '',
    email: c.email ?? '', phone: c.phone ?? '',
    address_line1: c.address_line1 ?? '', address_line2: c.address_line2 ?? '',
    city: c.city ?? '', state: c.state ?? '', postal_code: c.postal_code ?? '', country: c.country ?? '',
    birth_month: c.birth_month ?? null, birth_day: c.birth_day ?? null, birth_year: c.birth_year ?? null,
    socials: (c.socials ?? []).map(s => ({ ...s })),
    special_dates: (c.special_dates ?? []).map(s => ({ ...s })),
    photo_s3_key: c.photo_s3_key ?? null,
    password: '',
  })
  displayNameTouched.value = true // don't auto-overwrite an existing display name
  modalOpen.value = true
}

const closeModal = () => { modalOpen.value = false }

// Auto-fill display name from first+last until the user edits it directly.
const syncDisplayName = () => {
  if (displayNameTouched.value) return
  form.name = [form.first_name, form.last_name].filter(Boolean).join(' ').trim()
}

// ── Read-only view (CL1) ───────────────────────────────────────────────────────
const viewOpen = ref(false)
const viewClient = ref<Client | null>(null)
const openView = (c: Client) => { viewClient.value = c; viewOpen.value = true }
const editFromView = () => { const c = viewClient.value; viewOpen.value = false; if (c) openEdit(c) }

// ── Special dates (CL5) ─────────────────────────────────────────────────────────
const addSpecialDate = () => form.special_dates.push({ label: '', date: '' })
const removeSpecialDate = (idx: number) => form.special_dates.splice(idx, 1)

// ── Save ─────────────────────────────────────────────────────────────────────
const save = async () => {
  ['email', 'phone', 'password', 'socials', 'special_dates'].forEach(touch)
  if (!isValid.value) return
  saving.value = true; modalError.value = ''

  const socials = form.socials.filter(s => s.platform && s.handle.trim())
  const special_dates = form.special_dates.filter(sd => sd.label.trim() && sd.date)

  const payload: Record<string, any> = {
    name: form.name || undefined,
    first_name: form.first_name || undefined,
    last_name: form.last_name || undefined,
    nickname: form.nickname || undefined,
    gender: form.gender || undefined,
    email: form.email || null,
    phone: form.phone || undefined,
    address_line1: form.address_line1 || undefined,
    address_line2: form.address_line2 || undefined,
    city: form.city || undefined,
    state: form.state || undefined,
    postal_code: form.postal_code || undefined,
    country: form.country || undefined,
    birth_month: form.birth_month ?? undefined,
    birth_day: form.birth_day ?? undefined,
    birth_year: form.birth_year != null && /^\d{4}$/.test(String(form.birth_year)) ? form.birth_year : undefined,
    socials,
    special_dates,
    photo_s3_key: form.photo_s3_key,
  }
  if (form.password) payload.password = form.password

  try {
    const res = isEdit.value
      ? await apiFetch(`/admin/clients/${editId.value}`, { method: 'PATCH', body: JSON.stringify(payload) })
      : await apiFetch('/admin/clients', { method: 'POST', body: JSON.stringify(payload) })
    const data = await res.json()
    if (!res.ok) { modalError.value = data.error || 'Save failed'; return }
    await loadClients()
    modalOpen.value = false
  } catch {
    modalError.value = 'Network error'
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
/* ── Inline list (CL1) ── */
.cl-list { display: flex; flex-direction: column; gap: 0.5rem; }
.cl-row {
  display: flex; align-items: center; gap: 0.85rem;
  padding: 0.6rem 0.75rem;
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md, 10px);
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;
}
.cl-row:hover { border-color: var(--border-focus); background: var(--bg-surface-hover); }
.cl-row:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

.cl-thumb {
  flex: 0 0 auto; width: 2.6rem; height: 2.6rem; border-radius: 50%;
  overflow: hidden; background: var(--bg-elevated);
  display: flex; align-items: center; justify-content: center;
  color: var(--text-muted); font-size: 0.8rem; font-weight: 600;
}
.cl-thumb.lg { width: 4.5rem; height: 4.5rem; font-size: 1.25rem; }
.cl-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.cl-thumb-initials { user-select: none; }

.cl-main { flex: 1 1 auto; min-width: 0; }
.cl-name-line { display: flex; align-items: baseline; gap: 0.4rem; flex-wrap: wrap; }
.cl-name-line strong { font-size: 0.95rem; }
.cl-nick { color: var(--text-muted); font-weight: 400; font-size: 0.88rem; }
.cl-chip {
  font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.04em;
  padding: 1px 6px; border-radius: 999px; font-weight: 700;
}
.cl-chip-record { background: var(--bg-elevated); color: var(--text-muted); }
.cl-facts { display: flex; flex-wrap: wrap; gap: 0.1rem 0.85rem; margin-top: 0.15rem; }
.cl-fact { color: var(--text-secondary); font-size: 0.8rem; }

.cl-row-socials { flex: 0 0 auto; }
.cl-row-actions { flex: 0 0 auto; }

/* ── Read-only view ── */
.cl-view-head { display: flex; gap: 1rem; align-items: center; margin-bottom: 1rem; }
.cl-view-name { margin: 0 0 0.2rem; font-size: 1.1rem; }
.cl-view-sub { margin: 0 0 0.3rem; color: var(--text-muted); font-size: 0.85rem; }
.cl-view-grid {
  display: grid; grid-template-columns: auto 1fr; gap: 0.4rem 1rem; margin: 0;
}
.cl-view-grid dt { color: var(--text-muted); font-size: 0.8rem; }
.cl-view-grid dd { margin: 0; color: var(--text-primary); font-size: 0.88rem; }
.cl-muted { color: var(--text-muted); }
.cl-view-added { color: var(--text-muted); font-size: 0.75rem; margin: 1rem 0 0; }

/* ── Modal ── */
.cl-modal { max-width: 560px; }
/* Section spacing + dividers now live in FormSection.vue; the birthdate row
   layout lives in BirthdatePicker.vue. Only the section-title chrome (still used
   by the Special Dates header, which pairs the title with an inline button) and
   the shared two-column grid stay here. */
.cl-section-title {
  font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--text-muted); margin: 0 0 0.75rem;
}
.cl-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }

.cl-err { color: var(--danger-text); font-size: 0.75rem; margin-top: 0.25rem; display: block; }

/* Special dates (CL5) */
.cl-sd-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; }
.cl-sd-head .cl-section-title { margin: 0; }
.cl-sd-row { display: flex; gap: 0.5rem; margin-bottom: 0.5rem; align-items: center; }
.cl-sd-label {
  flex: 1; min-width: 0;
  padding: 0.65rem;
  /* Match the native <select> look (gender/month dropdowns) since this field is
     a datalist combo: standard field padding + a dropdown chevron on the right.
     Still a free-text input, so custom labels can be typed. */
  padding-right: 2rem;
  border: 1px solid var(--border-default); border-radius: var(--radius-sm);
  background: var(--bg-input); color: var(--text-primary); font-size: 0.9rem;
  appearance: none; -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.65rem center;
}
.cl-sd-date  { flex: 0 0 150px; padding: 0.65rem; border: 1px solid var(--border-default); border-radius: var(--radius-sm); background: var(--bg-input); color: var(--text-primary); font-size: 0.9rem; }
.cl-sd-label:focus, .cl-sd-date:focus { outline: none; border-color: var(--border-focus); }
.cl-social-remove {
  flex-shrink: 0; background: var(--danger-dim); color: var(--danger-text);
  border: none; border-radius: var(--radius-sm); width: 30px; height: 30px;
  cursor: pointer; font-size: 0.75rem;
}
.cl-social-remove:hover { opacity: 0.85; }
</style>
